import { QuietSwapTool, ToolSwapAction } from './ToolSwapAction';
/**
 * TwixT 盤 (24×24) コンポーネント。
 *
 * - 24×24 のペグ穴格子 (四隅は配置不可)。
 * - 自陣辺 (黒=上下、 白=左右) は薄い帯で常時表示。
 * - リンクは SVG `<line>` overlay で描画 (ペグ < リンク のレイヤ順)。
 * - クリックターゲットは「ペグ穴」 のみ。 リンク自動付与なので link UI は無し。
 * - 後手 2 手目に Swap 可能なら `twixt-swap-btn` を出す。
 *
 * MVP では Manual link mode / リーチ表示は未実装。 docs/games/twixt.md §5 参照。
 */

import {
  hasSwap,
  isCorner,
  isForbiddenFor,
  isOwnEdge,
  isPlace,
  legalKey,
  type Side,
  TWIXT_SIZE,
  type TwixtMove,
  type TwixtState,
} from '../../games/twixt';
import { usePendingConfirm } from '../../hooks/usePendingConfirm';
import { useFreshLastMove } from '../../lib/first-paint';

interface Props {
  state: TwixtState;
  legal?: TwixtMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: TwixtMove) => void;
  mini?: boolean;
  lastMove?: TwixtMove;
  staticView?: boolean;
  /** チュートリアル: 強調するペグ穴 [x, y] の配列。 */
  tutorialHighlight?: [number, number][];
}

const N = TWIXT_SIZE;

/** ペグ中心 (パーセント): セルの真ん中。 24 セルなので (i + 0.5) / 24 * 100。 */
function centerPct(i: number): number {
  return ((i + 0.5) / N) * 100;
}

export function TwixtBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
}: Props) {
  // 盤を開いた瞬間は直前手のリングを出さない (mount 後の新しい手だけアニメする)。
  const freshLast = useFreshLastMove(lastMove);
  const isMyTurn =
    myTurnAllowed !== undefined ? myTurnAllowed : yourSide != null && yourSide === state.side;

  const legalSet = new Set(
    (legal ?? []).filter(isPlace).map((m) => legalKey(m.Place.x, m.Place.y)),
  );
  const lastKey =
    lastMove && isPlace(lastMove) ? legalKey(lastMove.Place.x, lastMove.Place.y) : null;

  const showOppHint = !mini && !staticView && !isMyTurn && state.outcome == null;
  const swapAvailable = !mini && !staticView && yourSide != null && isMyTurn && state.outcome == null && onMove != null && hasSwap(legal);
  const { pending: pendingTap, tap: handleTap } = usePendingConfirm<string>(state);
  const pendingColor: 'First' | 'Second' = (yourSide ?? state.side) as 'First' | 'Second';

  // 着手 side のドット色: First (黒) / Second (白)
  const sideClass = state.side === 'First' ? 'side-black' : 'side-white';

  // リンクの線分は viewBox 0..N 系 (= ペグ中心 = i + 0.5)。
  // SVG は preserveAspectRatio="none" で盤の正方領域に貼る。
  //
  // ガイドの斜め 8 本: 4 隅 (= 各角の最外接 playable cell) から 2 本ずつ伸びる
  // knight-move ladder 方向 (gentle 1:2 / steep 2:1) の対角参照線。
  // gambiter.com TwixT rules で「四隅から伸びる diagonal guide lines は corner battle
  // の判定に便利」 と説明される慣例。 ペグ中心 = (i+0.5) 座標系で線を引く。
  // 角の playable cell は (1,1), (22,1), (1,22), (22,22)。
  const guideLines: Array<{ x1: number; y1: number; x2: number; y2: number }> = [
    // 左上 (1, 1) 起点
    { x1: 1 + 0.5, y1: 1 + 0.5, x2: 23 + 0.5, y2: 12 + 0.5 }, // gentle (+2,+1) → (23,12)
    { x1: 1 + 0.5, y1: 1 + 0.5, x2: 12 + 0.5, y2: 23 + 0.5 }, // steep  (+1,+2) → (12,23)
    // 右上 (22, 1) 起点
    { x1: 22 + 0.5, y1: 1 + 0.5, x2: 0 + 0.5, y2: 12 + 0.5 },  // gentle (-2,+1) → (0,12)
    { x1: 22 + 0.5, y1: 1 + 0.5, x2: 11 + 0.5, y2: 23 + 0.5 }, // steep  (-1,+2) → (11,23)
    // 左下 (1, 22) 起点
    { x1: 1 + 0.5, y1: 22 + 0.5, x2: 23 + 0.5, y2: 11 + 0.5 }, // gentle (+2,-1) → (23,11)
    { x1: 1 + 0.5, y1: 22 + 0.5, x2: 12 + 0.5, y2: 0 + 0.5 },  // steep  (+1,-2) → (12,0)
    // 右下 (22, 22) 起点
    { x1: 22 + 0.5, y1: 22 + 0.5, x2: 0 + 0.5, y2: 11 + 0.5 },  // gentle (-2,-1) → (0,11)
    { x1: 22 + 0.5, y1: 22 + 0.5, x2: 11 + 0.5, y2: 0 + 0.5 },  // steep  (-1,-2) → (11,0)
  ];

  return (
    <div className={`twixt-frame ${mini ? 'mini' : ''}`}>
      <div
        className={`twixt-board ${mini ? 'mini' : ''} ${sideClass} ${showOppHint ? 'not-my-turn' : ''}`}
      >
        {/* 自陣辺の帯 (黒=上下、 白=左右)。 視覚言語: 黒=上下、 白=左右。 */}
        <div className="twixt-edge twixt-edge-top" aria-hidden />
        <div className="twixt-edge twixt-edge-bottom" aria-hidden />
        <div className="twixt-edge twixt-edge-left" aria-hidden />
        <div className="twixt-edge twixt-edge-right" aria-hidden />

        {/* シチョウ (ladder) 参照用の斜め 8 本ガイド。 mini では非表示。 */}
        {!mini && (
          <svg
            className="twixt-guides"
            viewBox={`0 0 ${N} ${N}`}
            preserveAspectRatio="none"
            aria-hidden
          >
            {guideLines.map((g, i) => (
              <line
                key={`g${i}`}
                x1={g.x1}
                y1={g.y1}
                x2={g.x2}
                y2={g.y2}
                className="twixt-guide"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
        )}

        {/* ペグ穴格子: 576 個の button。 corner は非表示 + disabled。 */}
        <div className="twixt-grid">
          {Array.from({ length: N }, (_, y) =>
            Array.from({ length: N }, (_, x) => {
              const k = legalKey(x, y);
              const cell = state.board[y][x];
              const corner = isCorner(x, y);
              const isLegal = isMyTurn && legalSet.has(k);
              const isLast = !mini && lastKey === k;
              const isTutorialHint =
                tutorialHighlight?.some(([hx, hy]) => hx === x && hy === y) ?? false;
              const isPendingTap = !mini && pendingTap === k;
              const forbiddenForMe =
                yourSide != null && isForbiddenFor(yourSide, x, y);
              const ownEdge = yourSide != null && isOwnEdge(yourSide, x, y);
              const pegClass =
                cell === 'Black' ? 'peg-black' : cell === 'White' ? 'peg-white' : '';
              const pendingTapClass = isPendingTap
                ? pendingColor === 'First' ? 'pending-tap-black' : 'pending-tap-white'
                : '';
              const handler = () => {
                handleTap(k, isLegal, () => onMove?.({ Place: { x, y } }));
              };
              return (
                <button
                  key={k}
                  className={`twixt-hole ${pegClass} ${isLegal ? 'legal' : ''} ${isLast ? 'last-move' : ''} ${isLast && !freshLast ? 'no-anim' : ''} ${pendingTapClass} ${corner ? 'corner' : ''} ${forbiddenForMe && !corner ? 'forbidden-edge' : ''} ${ownEdge && !corner ? 'own-edge' : ''} ${isTutorialHint ? 'tutorial-hint' : ''}`}
                  onClick={handler}
                  disabled={mini || corner || !isLegal}
                  aria-label={`x ${x} y ${y}${cell !== 'Empty' ? ` ${cell}` : ''}`}
                  data-x={x}
                  data-y={y}
                >
                  {!corner && cell !== 'Empty' && <span className="peg" />}
                  {!corner && cell === 'Empty' && isPendingTap && <span className="pending-peg" aria-hidden />}
                </button>
              );
            }),
          )}
        </div>

        {/* リンク overlay (SVG)。 viewBox 0..N、 ペグ中心 = (x + 0.5, y + 0.5)。 */}
        <svg
          className="twixt-links"
          viewBox={`0 0 ${N} ${N}`}
          preserveAspectRatio="none"
          aria-hidden
        >
          {state.links.map((l, i) => {
            const cls = l.owner === 'First' ? 'link-black' : 'link-white';
            return (
              <line
                key={`${l.ax}-${l.ay}-${l.bx}-${l.by}-${i}`}
                x1={l.ax + 0.5}
                y1={l.ay + 0.5}
                x2={l.bx + 0.5}
                y2={l.by + 0.5}
                className={`twixt-link ${cls}`}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </svg>
      </div>
      {swapAvailable && <div className="tool-swap-row"><ToolSwapAction
        tool="peg" label="白で引き継ぐ" testId="twixt-swap" onClick={() => onMove?.('Swap')} /></div>}
      {!mini && !swapAvailable && yourSide != null && <QuietSwapTool tool={yourSide === 'First' ? 'peg-black' : 'peg'} />}
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}

// Keep linter happy about centerPct (kept for potential future overlay reuse).
void centerPct;
